// Phase Response Card 1.0 Lite — background renderer.
// Pure presentation. The RSVP variant component places this twice (once with
// target="section" and once with target="card"); the component short-circuits
// to null when its target doesn't match the config target — zero DOM when
// off, zero DOM for the non-matching variant.
//
// Custom-image graceful fallback: if the image 404s or fails to decode, we
// quietly hide the layer instead of showing a broken-image placeholder. The
// underlying card/section background remains visible.

import React, { useEffect, useRef, useState } from 'react';
import { RSVP_SURFACE_TEXTURES } from '../../../lib/utils/responseCardBackground';
import './RSVPBackgroundLayer.css';

export default function RSVPBackgroundLayer({ background, target, surfaceAlpha = 1 }) {
  const cfg = background || null;
  const wantedTarget = cfg?.target || 'card';
  const [imageBroken, setImageBroken] = useState(false);
  const lastProbedUrl = useRef(null);

  // Reset broken flag whenever the imageUrl changes.
  useEffect(() => {
    const url = cfg?.type === 'custom_image' ? cfg.imageUrl : null;
    if (url !== lastProbedUrl.current) {
      lastProbedUrl.current = url;
      setImageBroken(false);
    }
  }, [cfg?.type, cfg?.imageUrl]);

  if (!cfg || cfg.type === 'none') return null;
  if (wantedTarget !== target) return null;

  // Resolve URL per type.
  let imageUrl = null;
  let isSurface = false;
  if (cfg.type === 'surface_texture' && cfg.texture) {
    imageUrl = RSVP_SURFACE_TEXTURES[cfg.texture];
    isSurface = true;
  } else if (cfg.type === 'custom_image' && cfg.imageUrl && !imageBroken) {
    imageUrl = cfg.imageUrl;
  }
  if (!imageUrl) return null;

  const layerClass = isSurface
    ? 'rsvp-bg-layer rsvp-bg-layer--surface'
    : [
        'rsvp-bg-layer',
        `rsvp-bg-layer--image-${cfg.fit === 'contain' ? 'contain' : 'cover'}`,
        `rsvp-bg-layer--image-position-${['top', 'bottom'].includes(cfg.position) ? cfg.position : 'center'}`,
      ].join(' ');

  const baseOpacity = typeof cfg.opacity === 'number' ? Math.max(0, Math.min(1, cfg.opacity)) : 0.18;
  // Multiply by surfaceAlpha (1 = no change; 0 = fully hidden). Lets the
  // single Studio "Surface Opacity" slider fade textures/images uniformly.
  const opacity = baseOpacity * Math.max(0, Math.min(1, surfaceAlpha));

  // If the resulting opacity is zero, render nothing — keeps the DOM clean
  // and ensures no faint ghost remains in floating/no-card mode.
  if (opacity <= 0) return null;

  return (
    <>
      {/* Off-screen probe for graceful custom-image fallback. */}
      {!isSurface && cfg.imageUrl && (
        <img
          src={cfg.imageUrl}
          alt=""
          aria-hidden="true"
          style={{ display: 'none' }}
          onError={() => setImageBroken(true)}
        />
      )}
      <div
        className={layerClass}
        style={{ backgroundImage: `url(${imageUrl})`, opacity }}
        data-testid={`rsvp-bg-layer-${target}`}
      />
      {cfg.overlay && cfg.overlay !== 'none' && surfaceAlpha > 0 && (
        <div
          className={`rsvp-bg-overlay rsvp-bg-overlay--${cfg.overlay}`}
          style={surfaceAlpha < 1 ? { opacity: surfaceAlpha } : undefined}
          data-testid={`rsvp-bg-overlay-${target}`}
        />
      )}
    </>
  );
}
